import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { pending2FA, logout } from '../../features/user';
import Frame from '../widgets/Frame';

/**
 * החזרת מצב "ממתין לאימות" לצורך בדיקה, מלוח ניהול ה-SSO שב-CRM.
 *
 * ## מה זה עושה
 *
 * מקבל טוקן **ממתין** בכתובת, מניח אותו במקום שבו מסך ההתחברות מניח אותו, ושולח
 * את הדפדפן למסך הקוד. מכאן והלאה זו בדיוק הזרימה של הלקוח - אותו מסך, אותן
 * בקשות, אותן חסימות - וזו כל התכלית: לראות את מה שהלקוח רואה כשהוא אומר
 * "הקשתי את הקוד וזה לא עובד".
 *
 * ## למה זה בטוח
 *
 * ⚠️ **הטוקן הממתין אינו פותח דבר.** הוא אינו סשן - הוא מזהה אתגר - וכל מסלול
 * שאינו השלמת האימות דוחה אותו. מי שמחזיק אותו עומד בדיוק במקום שבו הלקוח עומד
 * אחרי הסיסמה, וצריך לעבור את אותו אימות בדיוק. הנפקתו דורשת הרשאה ייעודית ומפתח
 * אבטחה של הנציג, והיא נרשמת כאירוע חשוב בכרטיס הלקוח.
 *
 * ## מה שנוקה לפני
 *
 * `logout` לפני ההנחה, ולא אחרי: הדפדפן שמגיע לכאן הוא של הנציג, וסביר שיש בו
 * כבר טוקן - שלו או של בדיקה קודמת. טוקן ממתין שמונח מעל טוקן מחובר משאיר את
 * המסך במצב שאי אפשר להסביר, ובדיוק במסך שנועד להסביר.
 *
 * ⚠️ **הטוקן נמחק מהכתובת מיד.** הוא מסתיים בהיסטוריית הדפדפן, בכותרת ההפניה
 * ובכל תוסף שקורא כתובות; ההחלפה היא `replace`, כלומר גם חזרה אחורה לא תשחזר
 * אותו. אותו טיפול בדיוק שעושים `management-ivr` ו-myyemot בטוקן שבכתובת.
 */
export function AgentTestSession() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  /**
   * פעם אחת בלבד.
   *
   * ב-StrictMode של פיתוח האפקט רץ פעמיים, והשנייה כבר לא תמצא את הטוקן - הוא
   * הוסר מהכתובת - ולכן הייתה מפנה למסך ההתחברות מיד אחרי שהמצב הונח. הדגל הוא
   * `useRef` ולא `state`: הוא אינו אמור לגרום לציור מחדש.
   */
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;

    const token = params.get('token');
    if (!token) {
      navigate('/login', { replace: true });
      return;
    }

    dispatch(logout());
    dispatch(pending2FA({ token }));

    const url = new URL(window.location.href);
    url.searchParams.delete('token');
    window.history.replaceState({}, '', url.pathname + url.search);

    navigate('/login-verify', { replace: true, state: { methodUuid: '', trustedDevicesEnabled: false } });
  }, [params, navigate, dispatch]);

  return (
    <Frame>
      <div className='main'>
        <div className='main-top'>
          <h1>{t('One moment')}</h1>
        </div>
      </div>
    </Frame>
  );
}

export default AgentTestSession;
